import WebSocket from 'isomorphic-ws';
import { Operation as LoggingOperation, Log, LogCategory } from '@augloop/logging';
import { LogCountLimiter } from '@augloop/runtime-client';
export class WebSocketWorker {
    constructor() {
        this.hasBeenOpened = false;
        this.hadEgressError = false;
        this.lastEgressTime = 0;
        this.lastPongTime = 0;
        this.remainingPingFailures = 3;
        this.egressMessageCount = 0;
        this.egressByteCount = 0;
        this.isClosing = false;
        // All request messages will be put into this array before the WebSocket becomes open.
        this.pendingEgress = [];
    }
    init(url, ingress, onOpen, onClose) {
        this.ws = new WebSocket(url);
        this.logOp = new LoggingOperation({ operationName: WebSocketWorker.className, success: true, dimension1: 'V1' }).start();
        this.egressTimer = Date.now();
        this.egressMessageCountOp = new LoggingOperation({ operationName: 'WSEgressMessageCount', success: true, dimension1: 'V1' });
        this.egressByteCountOp = new LoggingOperation({ operationName: 'WSEgressByteOrderOfMagnitude', success: true, dimension1: 'V1' });
        this.ingressByteCountOp = new LoggingOperation({ operationName: 'WSIngressByteOrderOfMagnitude', success: true, dimension1: 'V1' });
        const logPingLatency = (success) => {
            if (!this.logPingLatencyOp) {
                return;
            }
            this.logPingLatencyOp.success = success;
            Log.info(0x1e4441de /* tag_4reh4 */, LogCategory.CoreDefault, this.logPingLatencyOp.stop());
            this.logPingLatencyOp = undefined;
        };
        this.ws.addEventListener('open', _event => {
            onOpen();
            WebSocketWorker.logCountLimiter.log(() => {
                this.logOp.resourceId = 'OnOpen';
                this.logOp.resultDescription = '';
                this.logOp.success = true;
                this.logOp.dimension0 = this.pendingEgress.length.toString();
                Log.info(0x1e4441dd /* tag_4reh3 */, LogCategory.CoreDefault, this.logOp.stop());
            });
            this.hasBeenOpened = true;
            this.pendingEgress.forEach(message => {
                this.egress({ obj: message });
            });
            this.pendingEgress = [];
            this.pingInterval = setInterval(() => {
                if (this.logPingLatencyOp) {
                    --this.remainingPingFailures;
                    logPingLatency(false);
                }
                if (this.remainingPingFailures > 0 && this.hasBeenOpened && this.lastPongTime < this.lastEgressTime) {
                    this.logPingLatencyOp = new LoggingOperation({
                        operationName: 'Ping',
                        success: true,
                        dimension0: this.ws.bufferedAmount >= 0 ? this.ws.bufferedAmount.toString().length.toString() : undefined,
                        dimension1: 'V1'
                    }).start();
                    this.ws.send(WebSocketWorker.pingPongMessage, (err) => {
                        if (err) {
                            --this.remainingPingFailures;
                            logPingLatency(false);
                        }
                    });
                }
            }, 30000);
        });
        this.ws.addEventListener('message', event => {
            if (this.logPingLatencyOp
                && typeof event.data === 'string'
                && event.data.length === 1
                && event.data[0] === WebSocketWorker.pingPongMessage) {
                logPingLatency(true);
                this.lastPongTime = Date.now();
            }
            else {
                this.logIngressCount(event.data);
                ingress(event.data);
            }
        });
        this.ws.addEventListener('error', event => {
            this.errorMessage = event.message;
            WebSocketWorker.logCountLimiter.log(() => {
                this.logOp.resourceId = 'OnError';
                this.logOp.resultDescription = this.errorMessage;
                this.logOp.success = false;
                Log.info(0x1e4441dc /* tag_4reh2 */, LogCategory.CoreDefault, this.logOp.stop());
            });
            this.ws.close();
        });
        this.ws.addEventListener('close', event => {
            WebSocketWorker.logCountLimiter.log(() => {
                this.logOp.resourceId = 'OnClose';
                this.logOp.resultDescription = event ? `code: ${event.code}. reason: ${event.reason}` : '';
                this.logOp.success = true;
                Log.info(0x1e4441db /* tag_4reh1 */, LogCategory.CoreDefault, this.logOp.stop());
            });
            if (this.pingInterval) {
                clearInterval(this.pingInterval);
                this.pingInterval = undefined;
            }
            onClose(this.errorMessage);
            this.isClosing = false;
        });
    }
    egress(message) {
        const obj = message.obj;
        this.logEgressCount((obj instanceof ArrayBuffer || obj instanceof Uint8Array) ? obj.byteLength : obj.length);
        this.lastEgressTime = Date.now();
        // Send the message directly when the WebSocket if it has been opened at some point,
        // otherwise, put the message into the pending queue.
        if (this.hasBeenOpened) {
            this.ws.send(obj, (err) => {
                if (err && !this.hadEgressError) {
                    this.hadEgressError = true;
                    WebSocketWorker.logCountLimiter.log(() => {
                        this.logOp.resourceId = 'OnFirstEgressError';
                        this.logOp.resultDescription = err.message;
                        this.logOp.success = false;
                        Log.info(0x1e4441da /* tag_4reh0 */, LogCategory.CoreDefault, this.logOp.stop());
                    });
                }
            });
        }
        else {
            this.pendingEgress.push(obj);
        }
    }
    close() {
        if (this.isClosing) {
            return;
        }
        this.isClosing = true;
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = undefined;
        }
        this.ws.close();
    }
    logEgressCount(byteCount) {
        const now = Date.now();
        const timeElapsedMs = now - this.egressTimer;
        if (timeElapsedMs > 1000) {
            if (this.egressMessageCount > 50) {
                this.egressMessageCountOp.start();
                this.egressMessageCountOp.resultDescription = 'Egress message count: ' + this.egressMessageCount.toString() + ', Buffered amount: ' + ((this.ws.bufferedAmount >= 0) ? this.ws.bufferedAmount.toString() : '-') + ', Time elapsed: ' + timeElapsedMs.toString();
                Log.info(0x1e4441d9 /* tag_4rehz */, LogCategory.CoreDefault, this.egressMessageCountOp.stop());
            }
            if (this.egressByteCount > 100000) {
                this.egressByteCountOp.start();
                this.egressByteCountOp.dimension2 = this.egressByteCount.toString().length.toString();
                this.egressMessageCountOp.resultDescription = 'Buffered amount: ' + ((this.ws.bufferedAmount >= 0) ? this.ws.bufferedAmount.toString() : '-') + ', Time elapsed: ' + timeElapsedMs.toString();
                Log.info(0x1e4441d8 /* tag_4rehy */, LogCategory.CoreDefault, this.egressByteCountOp.stop());
            }
            this.egressTimer = now;
            this.egressMessageCount = 0;
            this.egressByteCount = 0;
        }
        this.egressMessageCount++;
        this.egressByteCount += (byteCount !== null && byteCount !== void 0 ? byteCount : 0);
    }
    logIngressCount(obj) {
        const byteCount = obj.length;
        if (byteCount > 100000) {
            this.ingressByteCountOp.start();
            this.ingressByteCountOp.dimension2 = byteCount.toString().length.toString();
            Log.info(0x1e4441d7 /* tag_4rehx */, LogCategory.CoreDefault, this.ingressByteCountOp.stop());
        }
    }
}
WebSocketWorker.className = 'WebSocketWorker';
WebSocketWorker.logCountLimiter = new LogCountLimiter(WebSocketWorker.className);
WebSocketWorker.pingPongMessage = '~';
//# sourceMappingURL=websocket-worker.js.map